import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import { Shield, ArrowRight, Plus, Trash2, Pencil, Wand2, Loader2 } from "lucide-react";

export default function Phase3Controls() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, controls: contextControls, fetchDocuments, dataVersion } = useAudit();
  const [controls, setControls] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState("");
  const [editCtrl, setEditCtrl] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newCtrl, setNewCtrl] = useState({ control_id: "", source: "", clause_reference: "", description: "", mapped_process_area: "" });

  const fetchControls = useCallback(async () => {
    if (!auditRun?.id) return;
    const { data } = await API.get(`/audit-runs/${auditRun.id}/controls`);
    setControls(data);
  }, [auditRun?.id]);

  useEffect(() => {
    fetchControls();
  }, [fetchControls, dataVersion]);

  useEffect(() => {
    if (contextControls && contextControls.length > 0) {
      setControls(contextControls);
    }
  }, [contextControls]);

  const handleExtract = async () => {
    setExtracting(true);
    setExtractProgress("Starting control extraction...");
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/controls/extract`, {});
      const jobId = data?.job_id;
      if (!jobId) throw new Error("Missing extraction job id");

      const pollIntervalMs = 1500;
      const maxAttempts = 240;
      let attempts = 0;

      const pollJob = async () => {
        try {
          const { data: status } = await API.get(`/job-status/${jobId}`);

          if (status.state === "PENDING") {
            setExtractProgress("Queued for control extraction...");
          } else if (status.state === "PROGRESS") {
            const step = status.meta?.step;
            const doc = status.meta?.doc;
            if (step === "loading_docs") {
              setExtractProgress("Loading ready documents...");
            } else if (step === "batching") {
              setExtractProgress(doc ? `Batching sections from ${doc}...` : "Batching document sections...");
            } else if (step === "synthesizing" || step === "extracting") {
              setExtractProgress(doc ? `Extracting controls from ${doc}...` : "Extracting controls...");
            } else if (step === "deduplicating") {
              setExtractProgress("Deduplicating extracted controls...");
            } else if (step === "persisting") {
              setExtractProgress("Saving extracted controls...");
            }
          } else if (status.state === "SUCCESS") {
            const result = status.result || {};
            setExtracting(false);
            setExtractProgress("");
            await Promise.all([fetchControls(), fetchDocuments?.()]);

            const normalizedStatus = (result.status || "").toLowerCase();
            if (normalizedStatus === "success" || normalizedStatus === "ok") {
              const extractedCount = result.extracted || 0;
              if (extractedCount > 0) {
                toast.success(`Extracted ${extractedCount} controls`);
              } else {
                toast.info(result.message || "No controls were found in the documents. You can add controls manually.");
              }
            } else {
              toast.error(result.message || "Control extraction did not produce any controls.");
            }
            return;
          } else if (status.state === "FAILURE" || status.state === "REVOKED") {
            setExtracting(false);
            setExtractProgress("");
            toast.error(status.error || "Extraction job failed.");
            return;
          }

          attempts += 1;
          if (attempts >= maxAttempts) {
            setExtracting(false);
            setExtractProgress("");
            toast.error("Extraction timed out. Please check back in a moment.");
            return;
          }

          setTimeout(pollJob, pollIntervalMs);
        } catch (pollErr) {
          console.error("Failed to poll extraction status:", pollErr);
          setExtracting(false);
          setExtractProgress("");
          toast.error("Failed to check extraction status.");
        }
      };

      setTimeout(pollJob, pollIntervalMs);
    } catch (e) {
      console.error("Failed to start extraction:", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Extraction failed");
      setExtracting(false);
      setExtractProgress("");
    }
  };

  const handleAdd = async () => {
    try {
      await API.post(`/audit-runs/${auditRun.id}/controls`, newCtrl);
      setAddOpen(false);
      setNewCtrl({ control_id: "", source: "", clause_reference: "", description: "", mapped_process_area: "" });
      await fetchControls();
      toast.success("Control added");
    } catch (e) {
      toast.error("Failed to add control");
    }
  };

  const handleUpdate = async () => {
    if (!editCtrl) return;
    try {
      await API.put(`/audit-runs/${auditRun.id}/controls/${editCtrl.id}`, editCtrl);
      setEditCtrl(null);
      await fetchControls();
      toast.success("Control updated");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/audit-runs/${auditRun.id}/controls/${id}`);
      await fetchControls();
      toast.success("Control deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleContinue = async () => {
    await advancePhase(4);
    navigate("/phase/4");
  };

  return (
    <div className="space-y-6" data-testid="phase-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Phase 3: Control Mapping</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {extracting && extractProgress ? extractProgress : "Review and manage the Control Obligation Matrix."}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="add-control-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Control
          </Button>
          <Button size="sm" onClick={handleExtract} disabled={extracting} data-testid="extract-controls-btn">
            {extracting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
            Extract Controls
          </Button>
          <Button onClick={handleContinue} disabled={controls.length === 0} data-testid="continue-to-scope-top-btn">
            <ArrowRight className="w-4 h-4 mr-2" /> Continue to Scope Definition
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {controls.length === 0 ? (
            <div className="min-h-[300px] py-12 px-6 text-center text-muted-foreground flex flex-col items-center justify-center">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No controls yet. Use AI Extract or add manually.</p>
              <p className="text-xs mt-1 max-w-md">
                Once controls are extracted, this matrix will expand with source clauses, process mapping, and editable control descriptions.
              </p>
            </div>
          ) : (
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Control ID</TableHead>
                  <TableHead className="w-[120px]">Source</TableHead>
                  <TableHead className="w-[140px]">Clause</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[130px]">Process Area</TableHead>
                  <TableHead className="w-[70px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {controls.map((c) => (
                  <TableRow key={c.id} data-testid={`control-row-${c.control_id}`}>
                    <TableCell className="font-mono text-xs font-semibold">{c.control_id}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{c.source}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{c.clause_reference}</TableCell>
                    <TableCell className="text-xs leading-relaxed">{c.description}</TableCell>
                    <TableCell className="text-xs">{c.mapped_process_area}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditCtrl({ ...c })}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDelete(c.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={controls.length === 0} data-testid="continue-to-scope-btn">
          <ArrowRight className="w-4 h-4 mr-2" /> Continue to Scope Definition
        </Button>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Control</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Control ID</Label><Input value={newCtrl.control_id} onChange={(e) => setNewCtrl((p) => ({ ...p, control_id: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Source</Label><Input value={newCtrl.source} onChange={(e) => setNewCtrl((p) => ({ ...p, source: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Clause Reference</Label><Input value={newCtrl.clause_reference} onChange={(e) => setNewCtrl((p) => ({ ...p, clause_reference: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">Description</Label><Input value={newCtrl.description} onChange={(e) => setNewCtrl((p) => ({ ...p, description: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">Process Area</Label><Input value={newCtrl.mapped_process_area} onChange={(e) => setNewCtrl((p) => ({ ...p, mapped_process_area: e.target.value }))} className="mt-1" /></div>
            <Button onClick={handleAdd} className="w-full" data-testid="confirm-add-control-btn">Save Control</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editCtrl} onOpenChange={() => setEditCtrl(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Control</DialogTitle></DialogHeader>
          {editCtrl && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Control ID</Label><Input value={editCtrl.control_id} onChange={(e) => setEditCtrl((p) => ({ ...p, control_id: e.target.value }))} className="mt-1" /></div>
                <div><Label className="text-xs">Source</Label><Input value={editCtrl.source} onChange={(e) => setEditCtrl((p) => ({ ...p, source: e.target.value }))} className="mt-1" /></div>
              </div>
              <div><Label className="text-xs">Clause</Label><Input value={editCtrl.clause_reference} onChange={(e) => setEditCtrl((p) => ({ ...p, clause_reference: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Description</Label><Input value={editCtrl.description} onChange={(e) => setEditCtrl((p) => ({ ...p, description: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Process Area</Label><Input value={editCtrl.mapped_process_area} onChange={(e) => setEditCtrl((p) => ({ ...p, mapped_process_area: e.target.value }))} className="mt-1" /></div>
              <Button onClick={handleUpdate} className="w-full" data-testid="confirm-edit-control-btn">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
